// dropdown.ts — the console's seat on THE dropdown primitive.
//
// HISTORY, because this file used to say the opposite. R69 built a console
// dropdown as a deliberate TWIN of the Helm's `mkDropdown` (glance.ts):
// same listbox contract, twinned rather than shared because the two clients
// are separate bundles. Two implementations then drifted exactly the way
// twins do — the Helm's half never grew a keyboard lane, a focus-out
// dismiss, or type-ahead, and neither could theme its way out of a native
// <select> the audit still found 32 of.
//
// PR-3a retires the twin. There is ONE machine now
// (pi-stack/src/client/form-controls.ts, the shared client layer buttons.ts
// and tooltip.ts already live in) and this file is the console's ADAPTER on
// to it: it keeps `mkDropdown`'s signature, its `.hc-dd*` class names and
// therefore every selector its suites and e2e specs address — but those
// classes are LAYOUT hooks only. The paint moved to the byte-identical
// `fc-component-block` in both stylesheets, and primitives-check refuses a
// skin class that still carries background / color / border.
//
// Behaviour the adapter now inherits for free (it had none of it before):
// Home/End, printable-character type-ahead inside the open list, type-ahead
// from the CLOSED face, and Enter/Space to open. The preview lane (R70), the
// portal lane (bridge-ui-polish) and the exit discipline — every path out of
// an open list lands exactly one onPreviewEnd() — are unchanged, and live in
// the primitive now, so the Helm's dropdowns get them too.

import { mkFcSelect, type FcOption, type FcSelectCtl } from './form-controls.js';

export interface DropdownOption extends FcOption {}

export interface DropdownOpts {
  /** Lands on the BUTTON — the focusable, aria-carrying element. */
  id: string;
  options: () => DropdownOption[];
  value: () => string;
  onPick: (value: string) => void;
  /** Preview lane (R70): the option under pointer/focus. */
  onPreview?: (value: string) => void;
  /** Every exit from an active preview lands here exactly once. */
  onPreviewEnd?: () => void;
  /** Called on open so the host can close sibling popovers. */
  onOpen?: () => void;
  /** Portal lane: the pop re-parents to document.body on open and paints
   * position:fixed at the button's viewport rect. REQUIRED inside an AG Grid
   * cell — the cell clips and AG positions rows with transform:, so only a
   * body-parented pop escapes both. */
  portal?: boolean;
  /** Accessible name for the trigger, when no visible label sits beside it. */
  label?: string;
}

export interface DropdownCtl {
  el: HTMLElement;
  /** Re-read value() into the closed label. */
  set(): void;
  close(): void;
}

/** The console's `.hc-dd*` layout hooks, handed to the primitive as a skin.
 *  Stamped BESIDE the fc-* classes — never instead of them. */
export const HC_DD_SKIN = {
  wrap: 'hc-dd',
  btn: 'hc-dd-btn',
  label: 'hc-dd-label',
  chev: 'hc-dd-chev',
  pop: 'hc-dd-pop',
  item: 'hc-dd-item',
} as const;

export function mkDropdown(opts: DropdownOpts): DropdownCtl {
  const ctl: FcSelectCtl = mkFcSelect({
    id: opts.id,
    options: opts.options,
    value: opts.value,
    onPick: opts.onPick,
    onPreview: opts.onPreview,
    onPreviewEnd: opts.onPreviewEnd,
    onOpen: opts.onOpen,
    portal: opts.portal,
    label: opts.label,
    skin: HC_DD_SKIN,
  });
  return { el: ctl.el, set: () => ctl.set(), close: () => ctl.close() };
}
